import { useRouter } from "next/router";
import IsRtl from "./isRtl";
import { buildLocalePath, DEFAULT_LOCALE, stripLocalePrefix } from "lib/locale-urls";

const sitecorePublicTld =
  process.env.NODE_ENV === "production" ? ".sa" : ".com";

const extractRelativePath = (url) => {
  // Remove the protocol part (e.g., 'https://')
  let relativePath = "";

  // Extract the domain part (e.g., 'moc-adc.takamulstg.com' / prod: ...takamul.sa)
  if (url) {
    relativePath = url?.split(sitecorePublicTld)[1];
  }
  if (relativePath) {
    return process.env.NEXT_PUBLIC_SITECORE_REST + relativePath;
  } else {
    return url;
  }
};

const extractRelativeURLPath = (url) => {
  // Remove the protocol part (e.g., 'https://')
  let relativePath = "";

  // Extract the domain part (e.g., 'moc-adc.takamulstg.com' / prod: ...takamul.sa)
  if (url) {
    relativePath = url?.split(sitecorePublicTld)[1];
  }
  if (url?.includes("mulhem-client.takamul.info")) {
    return process.env.MULHEM_API + relativePath;
  } else if (relativePath) {
    return process.env.PUBLIC_URL + relativePath;
  } else {
    return url;
  }
};

const extractArInternalPath = (url) => {
  if (url && IsRtl()) {
    const path = url?.split(
      new RegExp(
        `${sitecorePublicTld.replace(".", "\\.")}/[Mm]inistries/[mM]oc/[hH]ome`
      )
    )[1];
    if (path) {
      const publicPath = buildLocalePath(
        DEFAULT_LOCALE,
        `/${(path || "").replace(/^\/+/, "")}`
      );
      return (process.env.PUBLIC_URL || "").replace(/\/$/, "") + publicPath;
    } else {
      return url;
    }
  } else return url;
};

function isExternalUrl(url) {
  const sitecoreHost = process.env.PUBLIC_URL;
  const sitecoreHost2 = process.env.NEXT_PUBLIC_SITECORE_API_HOST;
  if (!url) return false;

  // Consider URLs starting with the Sitecore host as relative (even though technically absolute)
  if (sitecoreHost && (url?.startsWith(sitecoreHost) || url?.startsWith(sitecoreHost2))) {
    return false;
  }

  // Normal check for absolute URLs
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

/**
 * Same path logic as CleanRelativeLink but pure — safe for click handlers and non-React code.
 * CleanRelativeLink must not be passed as a callback (it calls useRouter); use this with router.locale instead.
 */
function cleanRelativeLinkWithLocale(url, locale) {
  const loc = locale || DEFAULT_LOCALE;
  if (url) {
    let link = "";
    const fixedPart = "/ministries/moc/home";
    if (url?.toLowerCase().includes(fixedPart)) {
      const lowerUrl = url.toLowerCase();
      const fixedPartIndex = lowerUrl.indexOf(fixedPart);
      if (fixedPartIndex !== -1) {
        const afterFixedPart = url.substring(fixedPartIndex + fixedPart.length);
        return afterFixedPart || "/";
      }
    }
    if (isExternalUrl(url)) {
      return url;
    }

    try {
      const parsedUrl = new URL(url);
      const pathWithoutLocale = stripLocalePrefix(parsedUrl.pathname);
      link = buildLocalePath(loc, pathWithoutLocale) + (parsedUrl?.search ?? "");
    } catch (e) {
      if (!url?.startsWith("/en/") && !url?.startsWith("/ar/") && !url?.startsWith("/zh/") && !url?.startsWith("/ko/")) {
        const normalizedUrl = url?.startsWith("/") ? url.slice(1) : url;
        const pathWithoutLocale = normalizedUrl ? `/${normalizedUrl}` : "/";
        return buildLocalePath(loc, pathWithoutLocale);
      } else {
        return buildLocalePath(loc, stripLocalePrefix(url.split("?")[0]));
      }
    }

    return link;
  }
  return "";
}

function CleanRelativeLink(url) {
  const router = useRouter();
  return cleanRelativeLinkWithLocale(url, router.locale);
}

/**
 * Builds a clean path by replacing the locale prefix and preserving query and hash.
 * @param asPath - The original `router.asPath` string
 * @param currentLocale - The current locale (to be replaced)
 * @returns The cleaned path string without the locale prefix
 */
function buildRedirectPath(asPath, currentLocale) {
  const [pathAndHash, queryString = ""] = asPath.split("?");
  const hashIndex = pathAndHash.indexOf("#");

  const pathname = hashIndex >= 0 ? pathAndHash.slice(0, hashIndex) : pathAndHash;
  const hash = hashIndex >= 0 ? pathAndHash.slice(hashIndex) : "";

  const cleanedPath = stripLocalePrefix(pathname);
  const newPath = `${cleanedPath}${queryString ? `?${queryString}` : ""}${hash}`;

  return newPath;
}

export {
  extractRelativePath,
  extractArInternalPath,
  extractRelativeURLPath,
  cleanRelativeLinkWithLocale,
  CleanRelativeLink,
  buildRedirectPath,
  isExternalUrl,
};
